export interface InspectorSection {
  label: string;
  relationship?: string;
  sections?: InspectorSection[];
}

export const GRAPH_RENDER_LIMIT = 1000;

export const SOURCE_KINDS = new Set(["Base", "AZBase", "Meta", "MetaDetail"]);

export const TAG_KIND_PREFIX = "Tag_";

export const ZONE_TAG_TYPE = 1;

export const LABEL_TAG_TYPE = 2;

export const AD_ENTITY_TYPES: Record<string, string> = {
  "User": "users",
  "Computer": "computers",
  "Group": "groups",
  "Domain": "domains",
  "GPO": "gpos",
  "OU": "ous",
  "Container": "containers",
  "AIACA": "aiacas",
  "RootCA": "rootcas",
  "EnterpriseCA": "enterprisecas",
  "NTAuthStore": "ntauthstores",
  "CertTemplate": "certtemplates",
  "IssuancePolicy": "issuancepolicies",
  "Site": "sites",
  "SiteServer": "siteservers",
  "SiteSubnet": "sitesubnets",
  "Base": "base",
  "ADLocalGroup": "base",
  "ADLocalUser": "base"
};

export const AD_INSPECTOR_SECTIONS: Record<string, InspectorSection[]> = {
  "base": [
    {
      label: "Outbound Object Control",
      relationship: "controllables"
    },
    {
      label: "Inbound Object Control",
      relationship: "controllers"
    }
  ],
  "containers": [
    {
      label: "Inbound Object Control",
      relationship: "controllers"
    }
  ],
  "aiacas": [
    {
      label: "Inbound Object Control",
      relationship: "controllers"
    },
    {
      label: "PKI Hierarchy",
      relationship: "pki-hierarchy"
    }
  ],
  "certtemplates": [
    {
      label: "Inbound Object Control",
      relationship: "controllers"
    },
    {
      label: "Published To CAs",
      relationship: "published-to-cas"
    }
  ],
  "computers": [
    {
      label: "Sessions",
      relationship: "sessions"
    },
    {
      label: "Local Admins",
      relationship: "admin-users"
    },
    {
      label: "Inbound Execution Privileges",
      sections: [
        {
          label: "RDP Users",
          relationship: "rdp-users"
        },
        {
          label: "PSRemote Users",
          relationship: "ps-remote-users"
        },
        {
          label: "DCOM Users",
          relationship: "dcom-users"
        },
        {
          label: "SQL Admin Users",
          relationship: "sql-admins"
        },
        {
          label: "Constrained Delegation Users",
          relationship: "constrained-users"
        }
      ]
    },
    {
      label: "Member Of",
      relationship: "group-membership"
    },
    {
      label: "Local Admin Privileges",
      relationship: "admin-rights"
    },
    {
      label: "Outbound Execution Privileges",
      sections: [
        {
          label: "RDP Privileges",
          relationship: "rdp-rights"
        },
        {
          label: "PSRemote Rights",
          relationship: "ps-remote-rights"
        },
        {
          label: "DCOM Privileges",
          relationship: "dcom-rights"
        },
        {
          label: "Constrained Delegation Privileges",
          relationship: "constrained-delegation-rights"
        }
      ]
    },
    {
      label: "Inbound Object Control",
      relationship: "controllers"
    },
    {
      label: "Outbound Object Control",
      relationship: "controllables"
    }
  ],
  "domains": [
    {
      label: "Foreign Members",
      sections: [
        {
          label: "Foreign Users",
          relationship: "foreign-users"
        },
        {
          label: "Foreign Groups",
          relationship: "foreign-groups"
        },
        {
          label: "Foreign Admins",
          relationship: "foreign-admins"
        },
        {
          label: "Foreign GPO Controllers",
          relationship: "foreign-gpo-controllers"
        }
      ]
    },
    {
      label: "Inbound Trusts",
      relationship: "inbound-trusts"
    },
    {
      label: "Outbound Trusts",
      relationship: "outbound-trusts"
    },
    {
      label: "Controllers",
      relationship: "controllers"
    },
    {
      label: "ADCS Escalations",
      relationship: "adcs-escalations"
    }
  ],
  "enterprisecas": [
    {
      label: "Inbound Object Control",
      relationship: "controllers"
    },
    {
      label: "PKI Hierarchy",
      relationship: "pki-hierarchy"
    },
    {
      label: "Published Templates",
      relationship: "published-templates"
    }
  ],
  "gpos": [
    {
      label: "Affected Objects",
      sections: [
        {
          label: "OUs",
          relationship: "ous"
        },
        {
          label: "Computers",
          relationship: "computers"
        },
        {
          label: "Users",
          relationship: "users"
        },
        {
          label: "Sites",
          relationship: "sites"
        },
        {
          label: "Tier Zero Objects",
          relationship: "tier-zero"
        }
      ]
    },
    {
      label: "Inbound Object Control",
      relationship: "controllers"
    }
  ],
  "groups": [
    {
      label: "Sessions",
      relationship: "sessions"
    },
    {
      label: "Members",
      relationship: "members"
    },
    {
      label: "Member Of",
      relationship: "memberships"
    },
    {
      label: "Local Admin Privileges",
      relationship: "admin-rights"
    },
    {
      label: "Execution Privileges",
      sections: [
        {
          label: "RDP Privileges",
          relationship: "rdp-rights"
        },
        {
          label: "DCOM Privileges",
          relationship: "dcom-rights"
        },
        {
          label: "PSRemote Rights",
          relationship: "ps-remote-rights"
        }
      ]
    },
    {
      label: "Inbound Object Control",
      relationship: "controllers"
    },
    {
      label: "Outbound Object Control",
      relationship: "controllables"
    }
  ],
  "ntauthstores": [
    {
      label: "Inbound Object Control",
      relationship: "controllers"
    },
    {
      label: "Trusted CAs",
      relationship: "trusted-cas"
    }
  ],
  "ous": [
    {
      label: "Affecting GPOs",
      relationship: "gpos"
    },
    {
      label: "Groups",
      relationship: "groups"
    },
    {
      label: "Computers",
      relationship: "computers"
    },
    {
      label: "Users",
      relationship: "users"
    }
  ],
  "rootcas": [
    {
      label: "Inbound Object Control",
      relationship: "controllers"
    },
    {
      label: "PKI Hierarchy",
      relationship: "pki-hierarchy"
    }
  ],
  "issuancepolicies": [
    {
      label: "Inbound Object Control",
      relationship: "controllers"
    },
    {
      label: "Linked Certificate Templates",
      relationship: "linkedtemplates"
    }
  ],
  "sites": [
    {
      label: "Inbound Object Control",
      relationship: "controllers"
    },
    {
      label: "Linked GPOs",
      relationship: "linked-gpos"
    },
    {
      label: "Linked Site Servers",
      relationship: "siteservers"
    },
    {
      label: "Linked Site Subnets",
      relationship: "sitesubnets"
    }
  ],
  "users": [
    {
      label: "Sessions",
      relationship: "sessions"
    },
    {
      label: "Member Of",
      relationship: "memberships"
    },
    {
      label: "Local Admin Privileges",
      relationship: "admin-rights"
    },
    {
      label: "Execution Privileges",
      sections: [
        {
          label: "RDP Privileges",
          relationship: "rdp-rights"
        },
        {
          label: "PSRemote Privileges",
          relationship: "ps-remote-rights"
        },
        {
          label: "DCOM Privileges",
          relationship: "dcom-rights"
        },
        {
          label: "SQL Admin Rights",
          relationship: "sql-admin-rights"
        },
        {
          label: "Constrained Delegation Privileges",
          relationship: "constrained-delegation-rights"
        }
      ]
    },
    {
      label: "Outbound Object Control",
      relationship: "controllables"
    },
    {
      label: "Inbound Object Control",
      relationship: "controllers"
    }
  ],
  "siteservers": [],
  "sitesubnets": []
};

export const PROPERTY_LABELS: Record<string, string> = {
  "adcswebenrollmenthttp": "ADCS Web Enrollment HTTP",
  "adcswebenrollmenthttps": "ADCS Web Enrollment HTTPS",
  "adcswebenrollmenthttpsepa": "ADCS Web Enrollment HTTPS EPA",
  "admincount": "Admin Count",
  "adminsdholderprotected": "AdminSDHolder Protected",
  "applicationpolicies": "Application Policies Required",
  "authenticationenabled": "Authentication Enabled",
  "authorizedsignatures": "Authorized Signatures Required",
  "basicconstraintpathlength": "Basic Constraint Path Length",
  "blocksinheritance": "Blocks GPO Inheritance",
  "caname": "CA Name",
  "casecuritycollected": "CA Security Collected",
  "certchain": "Certificate Chain",
  "certificateapplicationpolicy": "Application Policy Extensions",
  "certificatemappingmethods": "Certificate Mapping Methods",
  "certificatemappingmethodsraw": "Certificate Mapping Methods (Raw)",
  "certificatenameflag": "Certificate Name Flags",
  "certificatepolicy": "Issuance Policy Extensions",
  "certname": "Certificate Name",
  "certtemplateoid": "Certificate Template OID",
  "certthumbprint": "Certificate Thumbprint",
  "certthumbprints": "Certificate Thumbprints",
  "clientallowedntlmservers": "Client Allowed NTLM Servers",
  "collected": "Collected",
  "compositionid": "Composition ID",
  "crosscertificatepair": "Cross Certificate Pair",
  "customexplicitdenyacescount": "Custom Explicit Deny ACEs Count",
  "custominheriteddenyacescount": "Custom Inherited Deny ACEs Count",
  "department": "Department",
  "description": "Description",
  "displayname": "Display Name",
  "distinguishedname": "Distinguished Name",
  "dnshostname": "DNS Hostname",
  "doesanyacegrantownerrights": "Does Any ACE Grant Owner Rights",
  "doesanyinheritedacegrantownerrights": "Does Any Inherited ACE Grant Owner Rights",
  "domain": "Domain FQDN",
  "domainsid": "Domain SID",
  "dontreqpreauth": "Do Not Require Pre-Authentication",
  "dsheuristics": "DSHeuristics",
  "effectiveekus": "Effective EKUs",
  "ekus": "Enhanced Key Usage",
  "email": "Email",
  "enabled": "Enabled",
  "enablesecuritysignature": "Enable Security Signature",
  "encryptedtextpwdallowed": "Password Stored Using Reversible Encryption",
  "enforced": "Enforced",
  "enrolleesuppliessubject": "Enrollee Supplies Subject",
  "enrollmentagentrestrictionscollected": "Enrollment Agent Restrictions Collected",
  "enrollmentflag": "Enrollment Flags",
  "expirepasswordsonsmartcardonlyaccounts": "Expire Passwords on Smart Card only Accounts",
  "firstseen": "First Seen by BloodHound",
  "flags": "Flags",
  "functionallevel": "Functional Level",
  "gmsa": "GMSA",
  "gpostatus": "GPO Status",
  "gpostatusraw": "GPO Status (Raw)",
  "grouplinkid": "Group Link ID",
  "groupscope": "Group Scope",
  "hasbasicconstraints": "Has Basic Constraints",
  "hascrosscertificatepair": "Has Cross Certificate Pair",
  "hasenrollmentagentrestrictions": "Has Enrollment Agent Restrictions",
  "haslaps": "LAPS Enabled",
  "hasspn": "Has SPN",
  "hasura": "Has User Rights Assignment Collection",
  "hasvulnerableendpoint": "Has Vulnerable Endpoint",
  "homedirectory": "Home Directory",
  "httpenrollmentendpoints": "HTTP Enrollment Endpoints",
  "httpsenrollmentendpoints": "HTTPS Enrollment Endpoints",
  "inheritancehash": "ACL Inheritance Hash",
  "inheritancehashes": "ACL Inheritance Hashes",
  "isOwnedObject": "Is Owned",
  "isTierZero": "Tier Zero",
  "isacl": "Is ACL",
  "isaclprotected": "ACL Inheritance Denied",
  "isdc": "Is Domain Controller",
  "isdeleted": "Is Deleted",
  "isinherited": "Is Inherited",
  "isprimarygroup": "Is Primary Group",
  "isreadonlydc": "Read-Only DC",
  "issuancepolicies": "Issuance Policies Required",
  "isuserspecifiessanenabled": "Is User Specifies San Enabled",
  "isuserspecifiessanenabledcollected": "Is User Specifies San Enabled Collected",
  "labels": "Labels",
  "lastcollected": "Last Collected by BloodHound",
  "lastlogon": "Last Logon",
  "lastlogontimestamp": "Last Logon (Replicated)",
  "lastseen": "Last Seen by BloodHound",
  "ldapavailable": "LDAP Available",
  "ldapsavailable": "LDAPS Available",
  "ldapsepa": "LDAPS EPA",
  "ldapsigning": "LDAP Signing",
  "lmcompatibilitylevel": "LM Compatibility Level",
  "lockedout": "Locked Out",
  "lockoutduration": "Lockout Duration",
  "lockoutobservationwindow": "Lockout Observation Window",
  "lockoutthreshold": "Lockout Threshold",
  "logonscriptenabled": "Logon Script Enabled",
  "logontype": "Logon Type",
  "machineaccountquota": "Machine Account Quota",
  "maxpwdage": "Maximum Password Age",
  "minpwdage": "Minimum Password Age",
  "minpwdlength": "Minimum password length",
  "msa": "MSA",
  "name": "Name",
  "netbios": "NetBIOS",
  "nodeType": "Node Type",
  "nosecurityextension": "No Security Extension",
  "ntlmminclientsec": "NTLM Min Client Sec",
  "ntlmminserversec": "NTLM Min Server Sec",
  "objectclass": "Object Class",
  "objectguid": "Object GUID",
  "objectid": "Object ID",
  "oid": "OID",
  "operatingsystem": "Operating System",
  "owner_objectid": "Owner Object ID",
  "ownersid": "Owner SID",
  "passwordcantchange": "User Cannot Change Password",
  "passwordexpired": "Password Expired",
  "passwordnotreqd": "Password Not Required",
  "primarykind": "Primary Kind",
  "pwdhistorylength": "Password History Length",
  "pwdlastset": "Password Last Set",
  "pwdneverexpires": "Password Never Expires",
  "pwdproperties": "Password Properties",
  "renewalperiod": "Renewal Period",
  "requiresecuritysignature": "Require Security Signature",
  "requiresmanagerapproval": "Requires Manager Approval",
  "restrictoutboundntlm": "Restrict Outbound NTLM",
  "restrictreceivingntmltraffic": "Restrict Receiving NTLM Traffic",
  "roleseparationenabled": "Role Separation Enabled",
  "roleseparationenabledcollected": "Role Separation Enabled Collected",
  "samaccountname": "SAM Account Name",
  "schannelauthenticationenabled": "Schannel Authentication Enabled",
  "schemaversion": "Schema Version",
  "sensitive": "Marked Sensitive",
  "serverreference": "Server Reference",
  "serviceprincipalnames": "Service Principal Names",
  "siteobject": "Site Object",
  "smartcardrequired": "Smartcard Required",
  "smbsigning": "SMB Signing",
  "spoofsidhistoryblocked": "Spoof SID History Blocked",
  "strongcertificatebindingenforcement": "Strong Certificate Binding Enforcement",
  "strongcertificatebindingenforcementraw": "Strong Certificate Binding Enforcement (Raw)",
  "subjectaltrequiredns": "Subject Alternative Name Require DNS",
  "subjectaltrequiredomaindns": "Subject Alternative Name Require Domain DNS",
  "subjectaltrequireemail": "Subject Alternative Name Require Email",
  "subjectaltrequirespn": "Subject Alternative Name Require SPN",
  "subjectaltrequireupn": "Subject Alternative Name Require UPN",
  "subjectrequireemail": "Subject Require Email",
  "supportedencryptiontypes": "Supported Kerberos Encryption Types",
  "system_tags": "Node System Tags",
  "tgtdelegation": "TGT Delegation",
  "title": "Title",
  "transitive": "Transitive",
  "trustattributesinbound": "Trust Attributes (Inbound)",
  "trustattributesoutbound": "Trust Attributes (Outbound)",
  "trustedtoauth": "Trusted For Constrained Delegation",
  "trusttype": "Trust Type",
  "unconstraineddelegation": "Allows Unconstrained Delegation",
  "unresolvedpublishedtemplates": "Unresolved Published Certificate Templates",
  "usedeskeyonly": "Use DES Key Only",
  "usemachineid": "Use Machine ID",
  "user_tags": "Node User Tags",
  "useraccountcontrol": "User Account Control",
  "validityperiod": "Validity Period",
  "vulnerablenetlogonsecuritydescriptor": "Vulnerable Netlogon Security Descriptor",
  "vulnerablenetlogonsecuritydescriptorcollected": "Vulnerable Netlogon Security Descriptor Collected",
  "webclientrunning": "WebClient Running",
  "whencreated": "Created",
  "zone": "Zone",
};

export const BASIC_FIELDS = ["zone", "labels", "nodeType", "isTierZero", "isOwnedObject", "displayname", "objectid"] as const;

export const HIDDEN_FIELDS = new Set([
  "name",
  "displayname",
  "objectid",
  "isTierZero",
  "isOwnedObject",
  "system_tags",
  "kinds",
  "nodetype",
  "zone",
]);

export const getEntityType = (kind?: string) => (kind ? AD_ENTITY_TYPES[kind] : undefined);

export const getSectionsForEntityType = (entityType?: string) =>
  entityType ? AD_INSPECTOR_SECTIONS[entityType] ?? [] : [];
